// Copyright (c) Docugami, Inc. All rights reserved.

import { ITooltipHostProps, TooltipHost } from "@fluentui/react";
import { DisabledCss } from "components/common/DgCommonStyledComponents";
import { dgFontFamily } from "components/common/Fonts";
import { primaryTheme } from "components/shell/presentation/AppStyles";
import { DgElementButton } from "components/shell/utils/DgIconButton";
import { IconDescriptor, IconRenderer } from "components/work_path/IconRenderer";
import { ExternalClickType, useExternalClickHandler } from "hooks/ExternalClickHandler";
import { useState, useMemo } from "react";
import styled, { css } from "styled-components";
import { Telemetry } from "telemetry/TelemetryManager";

export interface ICommand {
    canExecute: () => boolean;
    execute: () => Promise<void>;
    hidden?: boolean;
    tooltipContent?: string | JSX.Element | JSX.Element[];
    primary?: boolean;
}

export enum IconAlignment {
    Left,
    Right,
}

const LabelCss = css`
    padding: 2px 0.5em;
    border-radius: 2px;
`;

export const ButtonRoot = styled.div<{ disabled?: boolean, backgroundColor?: string, isColorLabel?: boolean, direction?: IconAlignment }>`
    display: flex;
    flex-direction: ${({ direction = IconAlignment }) => direction === IconAlignment.Left ? 'row-reverse' : 'row'};
    align-items: center;
    gap: 5px;
    cursor: pointer;
    color: black;
    ${props => !!props.backgroundColor && `background-color: ${props.backgroundColor}`};
    ${props => !!props.isColorLabel && LabelCss};
    ${props => !!props.disabled && DisabledCss};
    transition: all 0.3s ease-out;
    >.primaryFill {
        color: black;
    }
`

const LabelText = styled.span<{ color?: string, fontSize?: number }>`
    font-family: ${dgFontFamily};
    font-size: ${props => props.fontSize ? `${props.fontSize}px` : '10px'};
    font-weight: 500;
    color: ${props => props.color ?? ''};
`

export interface ICommandButton {
    icon?: IconDescriptor;
    label: string;
    showLabel?: boolean;
    labelColor?: string;
    backgroundColor?: string;
    canExecute: boolean;
    onClick?: () => void;
    tooltipProps?: ITooltipHostProps;
    isOverflow?: boolean;
}

export interface CommandButtonProps {
    command: ICommandButton;
    width?: number;
    height?: number;
    iconAlignment?: IconAlignment;
    fontSize?: number;
    dataAutomationId?: string;
}

export const CommandButton = (props: CommandButtonProps): JSX.Element => {
    if (isConfirmationButton(props.command)) {
        return (
            <ConfirmationCommandButton
                {...props}
                command={props.command} />
        )
    } else {
        return <CommandButtonContent {...props} />
    }
}

const CommandButtonContent = (props: CommandButtonProps): JSX.Element => {
    const onClick = () => {
        if (!!props.command.onClick) {
            Telemetry.trackNamedEvent(`${props.dataAutomationId}_Clicked`);
            props.command.onClick();
        }
    };

    return (
        <TooltipHost {...props.command.tooltipProps}>
            <DgElementButton onClick={onClick} disabled={!props.command.canExecute}>
                <ButtonRoot
                    data-automation-id={props.dataAutomationId}
                    direction={props.iconAlignment}
                    title={!props.command.tooltipProps ? props.command.label : ''}
                    backgroundColor={props.command.backgroundColor}
                    isColorLabel={props.command.showLabel && !!props.command.backgroundColor} >
                    {!!props.command.showLabel &&
                        <LabelText color={props.command.labelColor ?? props.command.icon?.defaultColor ?? primaryTheme.palette.themePrimary} fontSize={props.fontSize}>
                            {props.command.label.toLocaleUpperCase()}
                        </LabelText>
                    }
                    {
                        !!props.command.icon && (
                            <IconRenderer
                                source={props.command.icon}
                                width={props.width ?? props.fontSize ?? 12}
                                height={props.height ?? props.fontSize ?? 12} />
                        )
                    }
                </ButtonRoot>
            </DgElementButton>
        </TooltipHost>
    );
}

const CommandButtonRoot = styled.div`
    display: flex;
    align-self: center;
`

export interface IConfirmationCommandButton extends ICommandButton {
    confirmationLabel: string;
    confirmationColor?: string;
    confirmationBackgroundColor?: string;
}

const isConfirmationButton = (command: ICommandButton): command is IConfirmationCommandButton => 'confirmationLabel' in command;

interface ConfirmationCommandButtonProps extends CommandButtonProps {
    command: IConfirmationCommandButton;
}

export const ConfirmationCommandButton = (props: ConfirmationCommandButtonProps): JSX.Element => {
    const [buttonClicked, setButtonClicked] = useState(false);

    const buttonRef = useExternalClickHandler<HTMLDivElement>(ExternalClickType.Self, () => setButtonClicked(false), !buttonClicked);

    const command: ICommandButton = useMemo(() => {
        if (!buttonClicked) {
            return {
                ...props.command,
                onClick: () => {
                    Telemetry.trackNamedEvent(`${props.dataAutomationId}_Confirming`);
                    setButtonClicked(true);
                }
            };
        } else {
            return {
                ...props.command,
                icon: !!props.command.icon ? {
                    ...props.command.icon,
                    defaultColor: props.command.confirmationColor
                } : undefined,
                label: props.command.confirmationLabel,
                showLabel: true,
                backgroundColor: props.command.confirmationBackgroundColor,
                labelColor: props.command.confirmationColor,
                onClick: () => {
                    // The user is confirming the action
                    props.command.onClick?.();
                    Telemetry.trackNamedEvent(`${props.dataAutomationId}_Confirmed`);
                    setButtonClicked(false);
                }
            }
        }
    }, [buttonClicked, props]);

    return (
        <CommandButtonRoot ref={buttonRef}>
            <CommandButtonContent {...props} command={command} />
        </CommandButtonRoot>
    )
}
